/**
 * PortalLoginPage — tenant-branded login for the external client portal.
 *
 * Route: /portal/:tenant/login
 * Also accepts: /portal/login?tenant=plantek  (query param fallback)
 *
 * Flow:
 *   1. Read tenant slug from URL params or query string
 *   2. Fetch public theme via GET /api/themes/public/:tenant (no auth)
 *   3. Inject CSS variables (FOUC prevention — skeleton held until theme ready)
 *   4. Show ExternalLoginPanel with tenant branding
 *   5. After auth → navigate to ?redirect param → last view from localStorage → /my-views
 *
 * planTEK Phase 10d.4 — Branded portal entry experience.
 */
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { injectCSSVariables, DEFAULT_THEME } from '@/lib/theme';
import type { TenantTheme } from '@/lib/theme';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLoginPanel } from '@/components/external/ExternalLoginPanel';

/** localStorage key for the last external view token the user visited. */
const LAST_VIEW_KEY = 'portal_last_view';

export function PortalLoginPage() {
  const { tenant: tenantParam } = useParams<{ tenant: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Support /portal/:tenant/login (path) and /portal/login?tenant=xyz (query)
  const tenant = tenantParam ?? searchParams.get('tenant') ?? undefined;
  const redirectTo = searchParams.get('redirect') ?? undefined;

  const [themeReady, setThemeReady] = useState(false);
  const [branding, setBranding] = useState<TenantTheme['branding'] | undefined>();

  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Fetch and inject tenant theme before revealing the page (FOUC prevention).
  // Falls back to DEFAULT_THEME if tenant is unknown or theming is not enabled.
  useEffect(() => {
    if (!tenant) {
      injectCSSVariables(DEFAULT_THEME);
      setThemeReady(true);
      return;
    }
    api.externalViews.getPublicTheme(tenant)
      .then((theme) => {
        if (theme) {
          injectCSSVariables(theme);
          setBranding(theme.branding ?? undefined);
        } else {
          injectCSSVariables(DEFAULT_THEME);
        }
      })
      .catch(() => {
        injectCSSVariables(DEFAULT_THEME);
      })
      .finally(() => {
        setThemeReady(true);
      });
  }, [tenant]);

  // After auth is resolved and user is authenticated, redirect to the right place:
  //   1. ?redirect=... query param (e.g. specific view link from an email)
  //   2. Last visited view stored in localStorage
  //   3. /my-views (default landing for EXTERNAL users)
  useEffect(() => {
    if (!isAuthenticated || authLoading) return;

    if (redirectTo && redirectTo.startsWith('/')) {
      navigate(redirectTo, { replace: true });
      return;
    }

    const lastView = localStorage.getItem(LAST_VIEW_KEY);
    if (lastView) {
      navigate(`/ext/${lastView}`, { replace: true });
      return;
    }

    navigate('/my-views', { replace: true });
  }, [isAuthenticated, authLoading, redirectTo, navigate]);

  // Hold skeleton until theme is injected and Firebase auth is initialised.
  if (!themeReady || authLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        data-testid="portal-login-loading"
        style={{ backgroundColor: 'var(--background)' }}
      >
        <div className="w-full max-w-sm space-y-4">
          <Skeleton className="h-14 w-14 rounded-2xl mx-auto" />
          <Skeleton className="h-6 w-48 mx-auto" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // If already authenticated, redirect effect fires on next tick — show nothing.
  if (isAuthenticated) {
    return null;
  }

  return (
    <ExternalLoginPanel
      viewName=""
      branding={branding}
      data-testid="portal-login-panel"
    />
  );
}
